import classNames from 'classnames';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Button, { ButtonProps } from '@/components/molecules/button/button';
import CommaSeparatedList from '@/components/molecules/comma-separated-list/comma-separated-list';
import InspirationalSearchEntryPoint from '@/components/molecules/inspirational-search-entry-point/inspirational-search-entry-point';
import Link from '@/components/utilities/link/link';
import Modal from '@/components/utilities/modal/modal';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './search-bar-section-mobile-wrapper.module.scss';

export interface SearchBarSectionMobileWrapperProps {
  /** The label to show in the button in the footer */
  buttonLabel?: string;
  /** The inner content to be shown */
  children: React.ReactNode;
  /** The description to be shown in the header*/
  description?: string | string[];
  /** Indicates weather the element is visible */
  isOpen?: boolean;
  /** The label to be shown in the header*/
  label: string;
  /** An event that is triggered on clicking the back chevron */
  onClickBack?: ButtonProps['onClick'];
  /** An event that is triggered on clicking the clear link */
  onClickClear?: ButtonProps['onClick'];
  /** An event that is triggered on clicking the close button */
  onClickClose?: ButtonProps['onClick'];
  /** An event that is triggered on clicking the next button */
  onClickNext?: ButtonProps['onClick'];
  /** An event that is triggered on clicking the search button */
  onClickSearch?: ButtonProps['onClick'];
  /** The search settings */
  searchSettings?:
    | Content.Components['SearchSettingsDto']
    | Content.Components['TravelPortalSearchSettingsDto'];
  /** Indicates weather to trigger search on button click */
  shouldTriggerSearch?: boolean;
  /** Indicates weather to show the back button */
  showBackButton?: boolean;
}

const SearchBarSectionMobileWrapper = ({
  buttonLabel,
  children,
  description,
  isOpen,
  label,
  onClickBack,
  onClickClear,
  onClickClose,
  onClickNext,
  onClickSearch,
  searchSettings,
  showBackButton = false,
  shouldTriggerSearch = false,
}: SearchBarSectionMobileWrapperProps) => {
  const { t } = useI18n();

  const showInspirationalEntryPoint =
    searchSettings?.$type === 'SearchSettingsDto' &&
    searchSettings.searchBarInspirationalSearchEntryPoint;

  return (
    <Modal isOpen={isOpen}>
      <div className={styles.component}>
        <header className={styles.header}>
          <div
            className={classNames(styles.controlBar, {
              [styles.hasBackButton]: showBackButton,
            })}
          >
            {showBackButton && (
              <Link
                aria-label={t('items.common.words.back')}
                onClick={onClickBack}
              >
                <Icon className={styles.icon} name="ChevronLeft" size={20} />
              </Link>
            )}

            <Button icon="Close" onClick={onClickClose} variant="tertiary" />
          </div>

          <div className={styles.headline}>
            <div className={styles.text}>
              <Typography
                className={styles.label}
                tag="label"
                tagStyle="labelSmall"
              >
                {label}
              </Typography>

              {typeof description === 'string' && (
                <Typography className={styles.description} tagStyle="bodySmall">
                  {description}
                </Typography>
              )}

              {Array.isArray(description) && description.length > 0 && (
                <Typography className={styles.description} tagStyle="bodySmall">
                  <CommaSeparatedList list={description} />
                </Typography>
              )}
            </div>

            <Link onClick={onClickClear}>
              <Typography className={styles.link} tagStyle="bodySmall">
                {t('items.common.words.clear')}
              </Typography>
            </Link>
          </div>
        </header>

        <main className={styles.content}>{children}</main>

        <footer className={styles.footer}>
          <div className={styles.buttonWrapper}>
            <Button
              className={styles.button}
              onClick={shouldTriggerSearch ? onClickSearch : onClickNext}
              text={buttonLabel}
              variant="primary"
            />
          </div>

          {showInspirationalEntryPoint && (
            <InspirationalSearchEntryPoint
              {...searchSettings.searchBarInspirationalSearchEntryPoint}
              variant="solid"
            />
          )}
        </footer>
      </div>
    </Modal>
  );
};

SearchBarSectionMobileWrapper.displayName = 'SearchBarSectionMobileWrapper';

export default SearchBarSectionMobileWrapper;
